import { Box, HStack, useBreakpointValue } from "@chakra-ui/react"
import { CarouselGallery } from "@pathwright/ux/src/components/gallery/Gallery"
import { onMeasureType } from "@pathwright/ux/src/components/text/TruncatedHTML"
import { useEffect, useMemo, useRef, useState } from "react"
import { CoverPageOfferFragment as CoverPageOffer } from "@pathwright/graphql-client/src/generated"
import CoverPageDivider from "./CoverPageDivider"
import CoverPageOfferCard, {
  OFFER_BUTTON_HEIGHT,
  OFFER_HEADING_SPACER,
  OFFER_META_HEIGHT,
  OFFER_META_SPACER,
  OFFER_MULTIPLE_OPTIONS_HEIGHT,
  OFFER_PRICE_DESCRIPTION_HEIGHT,
  OFFER_PRICING_SPACER,
  OFFER_TITLE_HEIGHT
} from "./CoverPageOffer"
import { useCoverPageContext } from "./CoverPageProvider"
import { COVER_PAGE_PADDING } from "./constants"
import CoverPageSectionTrigger from "./design/CoverPageSectionTrigger"
import {
  offerHasPriceDescription,
  offerShowsBottomMetaRow,
  useCoverPageTranslate
} from "./utils"

const MAX_CARD_HEIGHT = 500

// Dynamic height orchestration for Offer cards
// This allows us to shrink all Offer cards down to the height
// of the tallest card, based on its body content
function useDynamicOfferHeights(
  offers: Array<CoverPageOffer> | undefined,
  galleryRef: React.RefObject<HTMLDivElement>
) {
  //
  // ────────────────────────────────────────────────
  // 1. State for natural measurements per offer
  // ────────────────────────────────────────────────
  //
  const [heights, setHeights] = useState<
    Record<
      number | string,
      {
        naturalBody: number
        naturalCard: number
      }
    >
  >({})

  //
  // Reset whenever offers change
  //
  useEffect(() => {
    setHeights({})
  }, [offers])

  //
  // Reset on width changes (re-flow of HTML text)
  //
  useEffect(() => {
    if (!galleryRef.current) return
    const ro = new ResizeObserver(() => {
      setHeights({})
    })
    ro.observe(galleryRef.current)
    return () => ro.disconnect()
  }, [galleryRef])

  //
  // ────────────────────────────────────────────────
  // 2. Utilities to compute chrome height
  //    (anything above/below the body)
  // ────────────────────────────────────────────────
  //
  const offersShowBottomMetaRow = useMemo(() => {
    return offers?.some((o) => offerShowsBottomMetaRow(o)) ?? false
  }, [offers])

  const offersHavePriceDescription = useMemo(() => {
    return offers?.some((o) => offerHasPriceDescription(o)) ?? false
  }, [offers])

  const offersHaveMultiplePriceOptions = useMemo(() => {
    return offers?.some((o) => (o.price_options?.length || 0) > 1) ?? false
  }, [offers])

  const titleHeight = OFFER_TITLE_HEIGHT
  const headingSpacer = OFFER_HEADING_SPACER
  const dividerHeight = 21
  const buttonHeight = OFFER_BUTTON_HEIGHT
  const priceDescriptionHeight = offersHavePriceDescription
    ? OFFER_PRICE_DESCRIPTION_HEIGHT
    : 0
  const pricingSpacer = offersHavePriceDescription ? OFFER_PRICING_SPACER : 0
  const multipleOptionsHeight = offersHaveMultiplePriceOptions
    ? OFFER_MULTIPLE_OPTIONS_HEIGHT
    : 0
  const multipleOptionsSpacer = offersHaveMultiplePriceOptions
    ? OFFER_PRICING_SPACER
    : 0
  const metaHeight = offersShowBottomMetaRow ? OFFER_META_HEIGHT : 0
  const metaSpacer = offersShowBottomMetaRow ? OFFER_META_SPACER : 0
  const allPadding = 64

  const chromeHeight =
    allPadding +
    titleHeight +
    headingSpacer +
    dividerHeight +
    multipleOptionsHeight +
    multipleOptionsSpacer +
    buttonHeight +
    priceDescriptionHeight +
    pricingSpacer +
    metaHeight +
    metaSpacer

  //
  // ────────────────────────────────────────────────
  // 3. Measurement handler for each card body
  // ────────────────────────────────────────────────
  //
  const onMeasureBodyFor =
    (id: number | string): onMeasureType =>
    (info) => {
      const naturalBody = info.naturalHeight
      const naturalCard = chromeHeight + naturalBody

      setHeights((prev) => {
        const existing = prev[id]
        if (
          existing &&
          existing.naturalBody === naturalBody &&
          existing.naturalCard === naturalCard
        ) {
          return prev
        }
        return {
          ...prev,
          [id]: { naturalBody, naturalCard }
        }
      })
    }

  //
  // ────────────────────────────────────────────────
  // 4. Derived collection-level measurements
  // ────────────────────────────────────────────────
  //
  const allMeasured = useMemo(() => {
    if (!offers) return false
    return offers.every((o) => heights[o.id] != null)
  }, [offers, heights])

  const tallestNaturalCard = useMemo(() => {
    if (!offers) return 0
    return offers.reduce(
      (max, o) => Math.max(max, heights[o.id]?.naturalCard ?? 0),
      0
    )
  }, [offers, heights])

  const tallestNaturalBody = useMemo(() => {
    if (!offers) return 0
    return offers.reduce(
      (max, o) => Math.max(max, heights[o.id]?.naturalBody ?? 0),
      0
    )
  }, [offers, heights])

  //
  // ────────────────────────────────────────────────
  // 5. Compute global rules
  //    Case A: any natural card > MAX_CARD_HEIGHT
  //    Case B: none exceed → shrink all to tallest natural card
  // ────────────────────────────────────────────────
  //
  const anyAboveMax = tallestNaturalCard > MAX_CARD_HEIGHT

  let dynamicMaxCardHeight: number = MAX_CARD_HEIGHT
  let forcedBodyHeight: number | undefined = undefined
  let bodyMaxHeightForTruncation: number | undefined = undefined

  if (!allMeasured) {
    //
    // Still measuring — default everything to max card height
    //
    dynamicMaxCardHeight = MAX_CARD_HEIGHT
    bodyMaxHeightForTruncation = MAX_CARD_HEIGHT - chromeHeight
    forcedBodyHeight = undefined
  } else if (anyAboveMax) {
    //
    // Case A — at least one card exceeds max height
    // → clamp every card to the max height
    // → clamp every body to the max-card budget
    //
    dynamicMaxCardHeight = MAX_CARD_HEIGHT
    bodyMaxHeightForTruncation = MAX_CARD_HEIGHT - chromeHeight
    forcedBodyHeight = undefined
  } else {
    //
    // Case B — none exceed max height
    // → shrink every card to the tallest natural card
    // → force every body height to the tallest natural body
    //
    dynamicMaxCardHeight = tallestNaturalCard
    forcedBodyHeight = tallestNaturalBody
    bodyMaxHeightForTruncation = undefined
  }

  //
  // Return the orchestration
  //
  return {
    offersShowBottomMetaRow,
    offersHavePriceDescription,
    offersHaveMultiplePriceOptions,
    dynamicMaxCardHeight,
    forcedBodyHeight,
    bodyMaxHeightForTruncation,
    onMeasureBodyFor,
    allMeasured,
    anyAboveMax,
    tallestNaturalCard,
    tallestNaturalBody
  }
}

const CoverPageOffers = () => {
  const { t } = useCoverPageTranslate()
  const offersSectionRef = useCoverPageContext((v) => v.offersSectionRef)
  const offers = useCoverPageContext((v) => v.offers)
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)
  const isPanelOpen = useCoverPageContext((v) => v.isDesignMode)
  const visibleItems = useBreakpointValue({
    base: 1,
    md: isPanelOpen ? 1 : 2,
    lg: isPanelOpen ? 1 : 3,
    xl: isPanelOpen ? 2 : 3
  })
  const galleryRef = useRef<HTMLDivElement>(null)
  const isMobile = useCoverPageContext((v) => v.isMobile)

  const {
    offersShowBottomMetaRow,
    offersHavePriceDescription,
    offersHaveMultiplePriceOptions,
    dynamicMaxCardHeight,
    forcedBodyHeight,
    bodyMaxHeightForTruncation,
    onMeasureBodyFor
  } = useDynamicOfferHeights(offers, galleryRef)

  if (!offers?.length) return null

  return (
    <>
      {!immersiveActive && (
        <CoverPageDivider title={t("join")} titleMargin="1rem" />
      )}
      <Box position="relative" w="100%" role="group" id="offers">
        <CoverPageSectionTrigger sectionKey="offers" top={"-40px"} />
        <HStack
          ref={offersSectionRef}
          w="100%"
          scrollMarginTop="150px"
          css={{
            "& .carousel-item": {
              maxW: "420px",
              overflowY: "visible",
              position: "relative",
              borderRadius: "var(--chakra-radii-card)"
            },
            "& .carousel-item-content": {
              borderRadius: "var(--chakra-radii-card)",
              overflow: "visible"
            }
          }}
        >
          <Box ref={galleryRef} w="100%">
            <CarouselGallery
              items={offers}
              renderItem={(offer) => (
                <CoverPageOfferCard
                  key={offer.id}
                  offer={offer}
                  displayMeta={offersShowBottomMetaRow}
                  displayDescription={offersHavePriceDescription}
                  reserveMultipleOptionsHeight={offersHaveMultiplePriceOptions}
                  maxCardHeight={dynamicMaxCardHeight}
                  forcedBodyHeight={forcedBodyHeight}
                  maxBodyHeightForTruncation={bodyMaxHeightForTruncation} // ← NEW
                  onMeasureBody={onMeasureBodyFor(offer.id)}
                />
              )}
              scrollBehavior={isMobile ? "smooth" : "snap"}
              itemsPerRow={visibleItems}
              maxItemHeight={`${dynamicMaxCardHeight}px`}
              gap={0}
              spacingY="0"
              spacingX={`calc(${COVER_PAGE_PADDING.md} - 1rem)`}
              enableStagger
            />
          </Box>
        </HStack>
      </Box>
    </>
  )
}

export default CoverPageOffers
